// Company-admin bulk employee import (main-app, Model A). Mirrors the platform
// admin-portal importer but in the main-app design system and scoped to the
// signed-in company via the admin's own token. Gated to employee:import.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Inbox, Download, Upload, CheckCircle2, ChevronDown, AlertTriangle, Loader2, ShieldAlert, ArrowLeft, ArrowRight, Users,
} from 'lucide-react';
import { useRbacRoles } from '@/rbac/useRbac';
import { Stepper } from '@/features/shared/components/Stepper';
import { hasPermission } from '@/rbac/rbac.generated';
import { parseCSV } from '@/lib/csv';
import {
  IMPORT_FIELDS, FieldGroup, importEmployees, TEMPLATES, ChildData, ImportResponse, RowResult,
} from '@/lib/employeeImportApi';

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const GROUP_LABEL_KEYS: Record<FieldGroup, string> = {
  employee: 'import.group_labels.employee',
  address: 'import.group_labels.address',
  emergency: 'import.group_labels.emergency',
  education: 'import.group_labels.education',
};

type ChildKind = 'addresses' | 'emergency_contacts' | 'education';
const CHILD_LABEL_KEYS: Record<ChildKind, string> = {
  addresses: 'import.child_labels.addresses',
  emergency_contacts: 'import.child_labels.emergency_contacts',
  education: 'import.child_labels.education',
};

const ACTION_BADGE: Record<string, string> = {
  create: 'bg-success-soft text-success-strong',
  update: 'bg-info-soft text-info-strong',
  skip: 'bg-danger-soft text-danger-strong',
};

const STEP_KEYS = ['import.steps.upload', 'import.steps.map', 'import.steps.preview', 'import.steps.done'];

function download(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const StatCard: React.FC<{ label: string; value: number; tone?: string }> = ({ label, value, tone }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${tone || 'text-foreground'}`}>{value}</div>
  </div>
);

const ResultsTable: React.FC<{ rows: RowResult[] }> = ({ rows }) => {
  const { t } = useTranslation('admin');
  return (
  <div className="overflow-x-auto rounded-xl border border-border">
    <table className="w-full text-sm">
      <thead className="bg-muted text-muted-foreground">
        <tr>
          <th className="text-left font-medium px-3 py-2 w-14">{t('import.table.row')}</th>
          <th className="text-left font-medium px-3 py-2">{t('import.table.name')}</th>
          <th className="text-left font-medium px-3 py-2">{t('import.table.email')}</th>
          <th className="text-left font-medium px-3 py-2 w-24">{t('import.table.action')}</th>
          <th className="text-left font-medium px-3 py-2">{t('import.table.issues')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((r) => (
          <tr key={r.row} className="text-foreground">
            <td className="px-3 py-2 text-muted-foreground">{r.row}</td>
            <td className="px-3 py-2">{r.name || <span className="text-muted-foreground">—</span>}</td>
            <td className="px-3 py-2">{r.email || <span className="text-muted-foreground">—</span>}</td>
            <td className="px-3 py-2">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${ACTION_BADGE[r.action]}`}>
                {r.action.toUpperCase()}
              </span>
            </td>
            <td className="px-3 py-2">
              {r.errors.length === 0 && r.warnings.length === 0 ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <div className="flex flex-col gap-0.5">
                  {r.errors.map((e, i) => <span key={`e${i}`} className="text-danger">{e}</span>)}
                  {r.warnings.map((w, i) => <span key={`w${i}`} className="text-warning">{w}</span>)}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const Summary: React.FC<{ res: ImportResponse }> = ({ res }) => {
  const { t } = useTranslation('admin');
  return (
  <div className="space-y-3">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <StatCard label={t('import.summary.total_rows')} value={res.summary.total} />
      <StatCard label={t('import.summary.to_create')} value={res.summary.to_create} tone="text-success" />
      <StatCard label={t('import.summary.to_update')} value={res.summary.to_update} tone="text-info" />
      <StatCard label={t('import.summary.skipped')} value={res.summary.skipped} tone={res.summary.skipped ? 'text-danger' : undefined} />
    </div>
    <div className="grid grid-cols-3 gap-3">
      <StatCard label={t('import.summary.addresses')} value={res.summary.addresses} />
      <StatCard label={t('import.summary.emergency_contacts')} value={res.summary.emergency_contacts} />
      <StatCard label={t('import.summary.education_records')} value={res.summary.education} />
    </div>
  </div>
  );
};

export const BulkImportPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const appPath = (p: string) => (tenantSlug ? `/t/${tenantSlug}/app/${p}` : `/app/${p}`);
  const roles = useRbacRoles();
  const canImport = hasPermission(roles, 'employee:import');

  const [current, setCurrent] = useState(0);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [childRows, setChildRows] = useState<ChildData>({});
  const [childNames, setChildNames] = useState<Record<ChildKind, string>>({ addresses: '', emergency_contacts: '', education: '' });
  const [dryResult, setDryResult] = useState<ImportResponse | null>(null);
  const [commitResult, setCommitResult] = useState<ImportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingKind, setLoadingKind] = useState<'validate' | 'import' | null>(null);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ employee: true });
  const [redirecting, setRedirecting] = useState(false);

  // After a successful import, take the admin to the Employee Directory so they see
  // the new team. Auto-redirects shortly; "Import another" cancels it.
  useEffect(() => {
    if (current === 3 && commitResult && redirecting) {
      const id = setTimeout(() => navigate(appPath('employees')), 4000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [current, commitResult, redirecting]); // eslint-disable-line react-hooks/exhaustive-deps

  const requiredKeys = IMPORT_FIELDS.filter((f) => f.required).map((f) => f.key);
  const requiredMapped = requiredKeys.every((k) => mapping[k]);

  const mappedRows = useMemo(
    () =>
      rawRows.map((r) => {
        const out: Record<string, string> = {};
        IMPORT_FIELDS.forEach((f) => {
          const col = mapping[f.key];
          if (col && r[col] != null && r[col] !== '') out[f.key] = r[col];
        });
        return out;
      }),
    [rawRows, mapping],
  );

  const handleMainFile = async (file: File) => {
    try {
      const { headers: h, rows } = parseCSV(await file.text());
      if (!h.length || !rows.length) { setNote({ kind: 'err', text: t('import.no_data_rows') }); return; }
      setFileName(file.name); setHeaders(h); setRawRows(rows);
      const guess: Record<string, string> = {};
      IMPORT_FIELDS.forEach((f) => {
        const match = h.find((col) => norm(col) === norm(f.key) || norm(col) === norm(f.label));
        if (match) guess[f.key] = match;
      });
      setMapping(guess); setDryResult(null); setCommitResult(null);
      setNote({ kind: 'ok', text: t('import.loaded_rows', { count: rows.length, file: file.name }) });
    } catch { setNote({ kind: 'err', text: t('import.read_error') }); }
  };

  const handleChildFile = (kind: ChildKind) => async (file: File) => {
    try {
      const { rows } = parseCSV(await file.text());
      setChildRows((c) => ({ ...c, [kind]: rows }));
      setChildNames((n) => ({ ...n, [kind]: file.name }));
      setNote({ kind: 'ok', text: t('import.loaded_child_rows', { count: rows.length, label: t(CHILD_LABEL_KEYS[kind]).toLowerCase() }) });
    } catch { setNote({ kind: 'err', text: t('import.read_error') }); }
  };

  const run = async (dry: boolean) => {
    setLoading(true); setLoadingKind(dry ? 'validate' : 'import'); setNote(null);
    try {
      const res = await importEmployees(mappedRows, dry, childRows);
      if (dry) { setDryResult(res); setCurrent(2); }
      else {
        setCommitResult(res); setCurrent(3); setRedirecting(true);
        setNote({ kind: 'ok', text: t('import.imported_summary', { created: res.summary.to_create, updated: res.summary.to_update }) });
      }
    } catch (e: any) {
      setNote({ kind: 'err', text: e?.response?.data?.detail || t('import.request_failed') });
    } finally { setLoading(false); setLoadingKind(null); }
  };

  const reset = () => {
    setRedirecting(false);
    setCurrent(0); setRawRows([]); setHeaders([]); setMapping({}); setFileName('');
    setChildRows({}); setChildNames({ addresses: '', emergency_contacts: '', education: '' });
    setDryResult(null); setCommitResult(null); setNote(null);
  };

  if (!canImport) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('import.no_permission_title')}</h1>
        <p className="text-muted-foreground mt-1">{t('import.no_permission_body')}</p>
      </div>
    );
  }

  const card = 'rounded-2xl border border-border bg-card p-6';
  const primaryBtn = 'h-10 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50';
  const ghostBtn = 'h-10 px-4 rounded-lg border border-border text-foreground hover:bg-muted inline-flex items-center gap-2';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Full-screen progress while a (potentially long) import runs. */}
      {loading && loadingKind === 'import' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="rounded-2xl border border-border bg-card px-8 py-7 text-center shadow-xl max-w-sm">
            <Loader2 className="w-9 h-9 mx-auto text-orange-500 animate-spin" />
            <div className="mt-4 font-semibold text-foreground">{t('import.importing_title', 'Importing employees…')}</div>
            <p className="mt-1 text-sm text-muted-foreground">{t('import.importing_body', 'This may take a moment. Please don’t close this tab.')}</p>
          </div>
        </div>
      )}

      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="w-4 h-4" /> {t('import.back')}
      </button>

      <div>
        <h1 className="text-h2 text-charcoal">{t('import.title')}</h1>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          {t('import.subtitle')}
        </p>
      </div>

      <Stepper steps={STEP_KEYS.map((k) => t(k))} current={current} />

      {note && (
        <div
          className={`rounded-lg px-3 py-2 text-sm border ${
            note.kind === 'ok'
              ? 'bg-success-soft border-success text-success-strong'
              : 'bg-danger-soft border-danger text-danger-strong'
          }`}
        >
          {note.text}
        </div>
      )}

      {/* Step 0: Upload */}
      {current === 0 && (
        <div className={card}>
          <label className="block cursor-pointer rounded-xl border-2 border-dashed border-border hover:border-orange-300 transition-colors p-10 text-center">
            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleMainFile(e.target.files[0])}
            />
            <Inbox className="w-9 h-9 mx-auto text-orange-500" />
            <div className="mt-3 font-medium text-foreground">{t('import.upload_prompt')}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {fileName ? t('import.loaded_file', { file: fileName, count: rawRows.length }) : t('import.upload_required_hint')}
            </div>
          </label>

          <div className="mt-3">
            <button className={ghostBtn} onClick={() => download('employees-template.csv', TEMPLATES.employees())}>
              <Download className="w-4 h-4" /> {t('import.employees_template')}
            </button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-muted-foreground">{t('import.optional_separator')}</span>
            <div className="flex-1 h-px bg-border" />
          </div>
          <p className="text-sm text-muted-foreground -mt-3 mb-4">
            {t('import.optional_note')}<code className="px-1 py-0.5 rounded bg-muted text-foreground">employee_email</code>.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(['addresses', 'emergency_contacts', 'education'] as ChildKind[]).map((kind) => (
              <div key={kind} className="rounded-xl border border-border p-4">
                <div className="font-medium text-foreground mb-3">{t(CHILD_LABEL_KEYS[kind])}</div>
                <label className={`${ghostBtn} w-full justify-center cursor-pointer`}>
                  <Upload className="w-4 h-4" /> {t('import.upload_csv')}
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleChildFile(kind)(e.target.files[0])}
                  />
                </label>
                <div className="mt-2 text-sm">
                  {childNames[kind] ? (
                    <span className="text-success">{childNames[kind]} ({(childRows[kind] || []).length})</span>
                  ) : (
                    <span className="text-muted-foreground">{t('import.not_added')}</span>
                  )}
                </div>
                <button
                  className="mt-1 text-sm text-orange-600 inline-flex items-center gap-1 hover:underline"
                  onClick={() => download(`${kind}-template.csv`, TEMPLATES[kind]())}
                >
                  <Download className="w-3.5 h-3.5" /> {t('import.template')}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <button className={primaryBtn} disabled={rawRows.length === 0} onClick={() => setCurrent(1)}>
              {t('import.next_map')}
            </button>
          </div>
        </div>
      )}

      {/* Step 1: Map columns */}
      {current === 1 && (
        <div className={card}>
          <p className="text-foreground mb-4">
            {t('import.map_intro')}{' '}
            <span className="text-red-600">*</span>{t('import.map_intro_suffix')}
          </p>

          <div className="space-y-3">
            {(['employee', 'address', 'emergency', 'education'] as FieldGroup[]).map((g) => {
              const open = !!openGroups[g];
              return (
                <div key={g} className="rounded-xl border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenGroups((o) => ({ ...o, [g]: !o[g] }))}
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted text-left"
                  >
                    <span className="font-medium text-foreground">{t(GROUP_LABEL_KEYS[g])}</span>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                      {IMPORT_FIELDS.filter((f) => f.group === g).map((f) => {
                        const missing = f.required && !mapping[f.key];
                        return (
                          <div key={f.key} className="flex items-center gap-3">
                            <div className="w-40 shrink-0">
                              <span className="text-sm font-medium text-foreground">{f.label}</span>
                              {f.required && <span className="text-red-600"> *</span>}
                              {f.hint && <div className="text-xs text-muted-foreground">{f.hint}</div>}
                            </div>
                            <select
                              value={mapping[f.key] || ''}
                              onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                              className={`flex-1 h-9 px-2 rounded-lg border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-orange-100 ${
                                missing ? 'border-red-300' : 'border-border'
                              }`}
                            >
                              <option value="">{t('import.not_mapped')}</option>
                              {headers.map((h) => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!requiredMapped && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-warning-soft border border-warning px-3 py-2 text-sm text-warning-strong">
              <AlertTriangle className="w-4 h-4" /> {t('import.map_required_warning')}
            </div>
          )}

          <div className="mt-6 flex items-center gap-2">
            <button className={ghostBtn} onClick={() => setCurrent(0)}>{t('import.back')}</button>
            <button className={primaryBtn} disabled={!requiredMapped || loading} onClick={() => run(true)}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t('import.validate_preview')}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {current === 2 && dryResult && (
        <div className={card}>
          <Summary res={dryResult} />
          {dryResult.summary.skipped > 0 && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-warning-soft border border-warning px-3 py-2 text-sm text-warning-strong">
              <AlertTriangle className="w-4 h-4" /> {t('import.skipped_warning', { count: dryResult.summary.skipped })}
            </div>
          )}
          {dryResult.child_issues.length > 0 && (
            <div className="mt-3 rounded-lg bg-warning-soft border border-warning px-3 py-2 text-sm text-warning-strong">
              <div className="font-medium">{t('import.child_skipped_warning', { count: dryResult.child_issues.length })}</div>
              <ul className="list-disc ml-5 mt-1">
                {dryResult.child_issues.map((i, idx) => <li key={idx}>{i}</li>)}
              </ul>
            </div>
          )}
          <div className="mt-4">
            <ResultsTable rows={dryResult.results} />
          </div>
          <div className="mt-6 flex items-center gap-2">
            <button className={ghostBtn} onClick={() => setCurrent(1)}>{t('import.back')}</button>
            <button
              className={primaryBtn}
              disabled={dryResult.summary.to_create + dryResult.summary.to_update === 0 || loading}
              onClick={() => run(false)}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {t('import.import_count', { count: dryResult.summary.to_create + dryResult.summary.to_update })}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Done */}
      {current === 3 && commitResult && (
        <div className={card}>
          <div className="flex items-start gap-2 rounded-lg bg-success-soft border border-success px-3 py-3 text-sm text-success-strong mb-4">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            <div>
              <div className="font-semibold">{t('import.import_complete')}</div>
              {t('import.import_complete_detail', {
                created: commitResult.summary.to_create,
                updated: commitResult.summary.to_update,
                addresses: commitResult.summary.addresses,
                emergency: commitResult.summary.emergency_contacts,
                education: commitResult.summary.education,
              })}
            </div>
          </div>
          <Summary res={commitResult} />
          <div className="mt-4">
            <ResultsTable rows={commitResult.results} />
          </div>
          {redirecting && (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('import.redirecting', 'Taking you to your Employee Directory…')}
            </div>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button className={primaryBtn} onClick={() => navigate(appPath('employees'))}>
              <Users className="w-4 h-4" /> {t('import.view_directory', 'View Employee Directory')} <ArrowRight className="w-4 h-4" />
            </button>
            <button className={ghostBtn} onClick={reset}>{t('import.import_another')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImportPage;
